import React, { useMemo, useState } from 'react';
import { IonSegment, IonSegmentButton, IonLabel } from '@ionic/react';
import ActionGrid from '@/components/common/ActionGrid';
import {
  PlanningGroup,
  PlanningGroupGridRow,
  PlanningInspectionPlanGridRow,
  PlanningLineItemGridRow,
} from '@/services/PlanningService';
import {
  buildPlanningGroupGridActionColumns,
  buildPlanningGroupGridBulkActions,
  buildPlanningGroupGridColumns,
  buildPlanningGroupGridData,
  buildPlanningGroupGridRows,
  buildPlanningInspectionPlanGridActionColumns,
  buildPlanningInspectionPlanGridBulkActions,
  buildPlanningInspectionPlanGridColumns,
  buildPlanningInspectionPlanGridData,
  buildPlanningInspectionPlanGridRows,
  buildPlanningLineItemGridActionColumns,
  buildPlanningLineItemGridBulkActions,
  buildPlanningLineItemGridColumns,
  buildPlanningLineItemGridData,
  buildPlanningLineItemGridRows,
} from '@/services/util/adapters/PlanningActionGridAdapter';

type PlanningGridTab = 'groups' | 'lineItems' | 'inspectionPlans';

interface PlanningGridViewProps {
  groups: PlanningGroup[];
  canAddGroup: boolean;
  canAddLineItem: boolean;
  canActivateLineItem: boolean;
  canActivateInspectionPlan: boolean;
  canLockInspectionPlan: boolean;
  canUnlockInspectionPlan: boolean;
  onEditGroup: (row: PlanningGroupGridRow) => void;
  onAddGroup: () => void;
  onEditLineItem: (row: PlanningLineItemGridRow) => void;
  onAddLineItem: (groupId?: number) => void;
  onActivateLineItems: (ids: number[]) => void;
  onDeactivateLineItems: (ids: number[]) => void;
  onEditInspectionPlan: (row: PlanningInspectionPlanGridRow) => void;
  onActivateInspectionPlans: (ids: number[]) => void;
  onDeactivateInspectionPlans: (ids: number[]) => void;
  onLockInspectionPlans: (ids: number[]) => void;
  onUnlockInspectionPlans: (ids: number[]) => void;
  onSwitchToAccordion: () => void;
}

const defaultColDef = { resizable: true, sortable: true, filter: true, minWidth: 90 };

const PlanningGridView: React.FC<PlanningGridViewProps> = ({
  groups,
  canAddGroup,
  canAddLineItem,
  canActivateLineItem,
  canActivateInspectionPlan,
  canLockInspectionPlan,
  canUnlockInspectionPlan,
  onEditGroup,
  onAddGroup,
  onEditLineItem,
  onAddLineItem,
  onActivateLineItems,
  onDeactivateLineItems,
  onEditInspectionPlan,
  onActivateInspectionPlans,
  onDeactivateInspectionPlans,
  onLockInspectionPlans,
  onUnlockInspectionPlans,
  onSwitchToAccordion,
}) => {
  const [activeTab, setActiveTab] = useState<PlanningGridTab>('lineItems');

  const groupRows = useMemo(() => buildPlanningGroupGridRows(groups), [groups]);
  const groupColumns = useMemo(() => buildPlanningGroupGridColumns(), []);
  const groupGridData = useMemo(() => buildPlanningGroupGridData(groupRows), [groupRows]);
  const groupActionColumns = useMemo(
    () => buildPlanningGroupGridActionColumns({ rows: groupRows, onEdit: onEditGroup }),
    [groupRows, onEditGroup]
  );
  const groupBulkActions = useMemo(
    () => buildPlanningGroupGridBulkActions({ canAddGroup, onAddNew: onAddGroup, onSwitchToAccordion }),
    [canAddGroup, onAddGroup, onSwitchToAccordion]
  );

  const lineItemRows = useMemo(() => buildPlanningLineItemGridRows(groups), [groups]);
  const lineItemColumns = useMemo(() => buildPlanningLineItemGridColumns(), []);
  const lineItemGridData = useMemo(() => buildPlanningLineItemGridData(lineItemRows), [lineItemRows]);
  const lineItemActionColumns = useMemo(
    () => buildPlanningLineItemGridActionColumns({ rows: lineItemRows, onEdit: onEditLineItem }),
    [lineItemRows, onEditLineItem]
  );
  const lineItemBulkActions = useMemo(
    () => buildPlanningLineItemGridBulkActions({
      canAddLineItem,
      canActivateLineItem,
      onAddNew: onAddLineItem,
      onActivate: onActivateLineItems,
      onDeactivate: onDeactivateLineItems,
      onSwitchToAccordion,
    }),
    [canAddLineItem, canActivateLineItem, onAddLineItem, onActivateLineItems, onDeactivateLineItems, onSwitchToAccordion]
  );

  const inspectionPlanRows = useMemo(() => buildPlanningInspectionPlanGridRows(groups), [groups]);
  const inspectionPlanColumns = useMemo(() => buildPlanningInspectionPlanGridColumns(), []);
  const inspectionPlanGridData = useMemo(() => buildPlanningInspectionPlanGridData(inspectionPlanRows), [inspectionPlanRows]);
  const inspectionPlanActionColumns = useMemo(
    () => buildPlanningInspectionPlanGridActionColumns({ rows: inspectionPlanRows, onEdit: onEditInspectionPlan }),
    [inspectionPlanRows, onEditInspectionPlan]
  );
  const inspectionPlanBulkActions = useMemo(
    () => buildPlanningInspectionPlanGridBulkActions({
      canActivateInspectionPlan,
      canLockInspectionPlan,
      canUnlockInspectionPlan,
      onActivate: onActivateInspectionPlans,
      onDeactivate: onDeactivateInspectionPlans,
      onLock: onLockInspectionPlans,
      onUnlock: onUnlockInspectionPlans,
      onSwitchToAccordion,
    }),
    [
      canActivateInspectionPlan,
      canLockInspectionPlan,
      canUnlockInspectionPlan,
      onActivateInspectionPlans,
      onDeactivateInspectionPlans,
      onLockInspectionPlans,
      onUnlockInspectionPlans,
      onSwitchToAccordion,
    ]
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', height: 'calc(100vh - 170px)' }}>
      <IonSegment
        value={activeTab}
        onIonChange={(e) => setActiveTab((e.detail.value as PlanningGridTab) ?? 'lineItems')}
        style={{ maxWidth: '600px' }}
      >
        <IonSegmentButton value="groups">
          <IonLabel style={{ lineHeight: '12px', fontSize: 'smaller' }}>Groups</IonLabel>
        </IonSegmentButton>
        <IonSegmentButton value="lineItems">
          <IonLabel style={{ lineHeight: '12px', fontSize: 'smaller' }}>Line Items</IonLabel>
        </IonSegmentButton>
        <IonSegmentButton value="inspectionPlans">
          <IonLabel style={{ lineHeight: '12px', fontSize: 'smaller' }}>Inspection Plans</IonLabel>
        </IonSegmentButton>
      </IonSegment>

      {activeTab === 'groups' && (
        <ActionGrid
          columns={groupColumns}
          gridData={groupGridData}
          actionColumns={groupActionColumns}
          bulkActions={groupBulkActions}
          defaultColDef={defaultColDef}
          overlayNoRowsTemplate="No groups found."
        />
      )}

      {activeTab === 'lineItems' && (
        <ActionGrid
          columns={lineItemColumns}
          gridData={lineItemGridData}
          actionColumns={lineItemActionColumns}
          bulkActions={lineItemBulkActions}
          defaultColDef={defaultColDef}
          overlayNoRowsTemplate="No line items found."
        />
      )}

      {activeTab === 'inspectionPlans' && (
        <ActionGrid
          columns={inspectionPlanColumns}
          gridData={inspectionPlanGridData}
          actionColumns={inspectionPlanActionColumns}
          bulkActions={inspectionPlanBulkActions}
          defaultColDef={defaultColDef}
          overlayNoRowsTemplate="No inspection plans found."
        />
      )}
    </div>
  );
};

export default PlanningGridView;
